import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useMediaQuery from "hooks/useMediaQuery";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Header from "../../components/Header";
import UsersApi from "../../services/api/users";
import { tokens } from "../../theme";

const UsersIndex = () => {
  const navigate = useNavigate();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const usersModule = UsersApi();
  const [usersList, setUsersList] = useState([]);

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");

  const isSmallScreen = useMediaQuery("(max-width: 767px)");

  const columns = [
    { field: "id", headerName: "ID" },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "contact",
      headerName: "Contact Number",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "",
      headerName: "Action",
      width: 200,
      renderCell: ({ row: { id } }) => {
        return (
          <Box
            display="flex"
            gap={2}
            justifyContent="center"
            borderRadius="4px"
          >
            <Box display="flex">
              <EditOutlinedIcon
                fontSize="small"
                style={{ marginRight: "4px" }}
              />
              <Typography
                variant="button"
                color="warning"
                onClick={() => editUser(id)}
                style={{ cursor: "pointer", marginRight: "10px" }}
              >
                Edit
              </Typography>
            </Box>

            <Box display="flex">
              <DeleteOutlineOutlinedIcon
                fontSize="small"
                color="error"
                style={{ marginRight: "4px" }}
              />
              <Typography
                variant="button"
                color="error"
                onClick={() => deleteUser(id)}
                style={{ cursor: "pointer" }}
              >
                Delete
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await usersModule.getAllUsers();

      setUsersList(data.users);
    } catch (error) {
      console.log("Not loading users", error);
    }
  };

  const createUser = () => {
    navigate("/create-user");
  };

  const editUser = (id) => {
    const user = usersList.find((user) => user.id === id);

    navigate("/create-user", { state: { user } });
  };

  const deleteUser = (id) => {
    setSelectedUserId(id);
    handleOpenConfirmation();
  };

  const handleOpenConfirmation = () => {
    setConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
  };

  const handleConfirmAction = () => {
    try {
      const response = usersModule.deleteUser(selectedUserId);

      console.log("User deleted successfully: ", response);
      fetchUsers();
    } catch (error) {
      console.log("Not deleted user. ", error);
    }

    handleCloseConfirmation();
  };

  return (
    <>
      <Box
        gap={2}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "15px 30px",
          borderBottom: "1px solid var(--color-primary-200)",
        }}
      >
        <Header title="Users" />
        <Box sx={{ borderLeft: "1px solid var(--color-primary-200)" }}>
          <Button
            type="submit"
            variant="text"
            onClick={createUser}
            sx={{ padding: "5px 15px" }}
            startIcon={<AddIcon />}
          >
            Create New User
          </Button>
        </Box>
      </Box>

      <Box m="30px" display="flex" flexDirection="column" flex={1}>
        <Box
          sx={{
            height: {
              xs: "100%",
              sm: 300,
              md: "calc(100vh - 65px - 50px - 60px)",
            },
          }}
        >
          <DataGrid
            rows={usersList}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            columnHeaderHeight={isSmallScreen ? 70 : 60}
            sx={{
              "& .MuiDataGrid-columnHeader--moving": {
                backgroundColor: "inherit !important",
              },
              ...(isSmallScreen && {
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-sortIcon": {
                  display: "none",
                },
                "& .MuiDataGrid-menuIcon": {
                  display: "none",
                },
                "& .MuiDataGrid-columnHeader": {
                  padding: "4px !important",
                  justifyContent: "center",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  padding: "0 !important",
                  margin: "0 !important",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "normal !important",
                  wordBreak: "normal",
                  textAlign: "center",
                  lineHeight: 1.2,
                },
              }),
            }}
          />
        </Box>
      </Box>

      <ConfirmationDialog
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmAction}
      />
    </>
  );
};

export default UsersIndex;
